import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { shared } from '../../config';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import { GLADLY_CHAT_FLAGSHIP_KEY, useExperiment } from '../../experiments';
import { ChatIcon } from './ChatIcon';
import { ButtonContainer, Button, UnreadMessageDot } from './styles';
import { OVERLAPING_PATH } from './constants';
import { userClickGladyChatButton } from './events';

export const ChatButton = ({
  gladlySettings: { displayRules = { rules: [] } } = {},
}) => {
  const reporter = useReporter();
  const { value: showGladly } = useExperiment(GLADLY_CHAT_FLAGSHIP_KEY, false);
  const [isGladlyInitiated, setIsGladlyInitiated] = useState(false);
  const [showUnreadMessageDot, setShowUnreadMessageDot] = useState(false);
  const {
    isLoading: isCustomerLoading,
    isAuthenticated,
    customer,
  } = useSessionCustomer();
  const router = useRouter();
  const findOverlapingPath = pathname => {
    if (pathname === undefined) {
      return false;
    }
    const result = OVERLAPING_PATH.find(path => pathname.indexOf(path) !== -1);
    return result !== undefined;
  };
  const isOverlaping = findOverlapingPath(router.asPath);

  //Checks if the current url is in the excluded list of Gladly settings page
  const isExcludedPath = displayRules?.rules.some(item =>
    router.asPath.includes(item.content.value),
  );

  useEffect(() => {
    // eslint-disable-next-line no-undef
    const routeChangeHandler = () => Gladly.navigate();

    // eslint-disable-next-line no-undef
    Gladly.init({
      autoShowButton: false,
      appId: shared.GLADLY_PUBLISHABLE_KEY,
    })
      .then(function () {
        // eslint-disable-next-line no-undef
        Gladly.on('message:received', () => {
          setShowUnreadMessageDot(true);
        });
        // eslint-disable-next-line no-undef
        Gladly.on('campaign:triggered', e => {
          setShowUnreadMessageDot(true);
          // Gladly.applyCampaign(e[0].id); // For future reference. May introduce this soon.
        });
        // eslint-disable-next-line no-undef
        Gladly.on('sidekick:opened', e => {
          setShowUnreadMessageDot(false);
        });

        setIsGladlyInitiated(true);
        router.events.on('routeChangeComplete', routeChangeHandler);
      })
      .catch(() => {});

    return () => router.events.off('routeChangeComplete', routeChangeHandler);
  }, [router.events]);

  useEffect(() => {
    const { Gladly } = window;

    const isGladlyAvailable = showGladly && Gladly;
    if (
      !isCustomerLoading &&
      customer &&
      isAuthenticated &&
      isGladlyAvailable &&
      isGladlyInitiated
    ) {
      const { first_name, email } = customer;
      Gladly.clearUser();
      Gladly.setUser({
        name: first_name,
        email,
      });
    }
  }, [
    customer,
    isAuthenticated,
    isCustomerLoading,
    showGladly,
    isGladlyInitiated,
  ]);

  const onChatButtonHandler = () => {
    reporter.tag(userClickGladyChatButton({ path: router.asPath }));
    const { Gladly } = window;
    if (Gladly.show) Gladly.show();
  };

  if (isExcludedPath || !isGladlyInitiated) {
    return <></>;
  }

  return (
    <ButtonContainer shown={showGladly} isOverlaping={isOverlaping}>
      <Button
        id={'custom-gladly-chat-button'}
        aria-label={
          'Click here to chat with a customer service representative.'
        }
        showUnreadMessageDot={showUnreadMessageDot}
        onClick={onChatButtonHandler}
      >
        <ChatIcon />
      </Button>

      {showUnreadMessageDot && <UnreadMessageDot />}
    </ButtonContainer>
  );
};

ChatButton.displayName = 'ChatButton';

ChatButton.propTypes = {
  gladlySettings: PropTypes.object,
};
